import clsx from 'clsx';
import { formatEther, parseEther, parseUnits } from 'ethers/lib/utils';
import { useMemo } from 'react';
import AssetType from '../../../components/AssetType';
import Button from '../../../components/Button';
import CustomModal from '../../../components/CustomModal';
import CustomModalContent from '../../../components/CustomModal/CustomModalContent';
import CustomModalHeader from '../../../components/CustomModal/CustomModalHeader';
import useModalState from '../../../context/modal/useModalState';
import {
    calculatePnl,
    formatAmountWithSuffix,
    formatPercentages,
    formatUSD,
} from '../../../helpers';
import { UserPosition } from '../../../hooks/web3/trade/useUserPositions';
import useTokenStats from '../../../hooks/web3/useTokenStats';
import useUserFee from '../../../hooks/web3/useUserFee';
import MarkPrice from '../../../pages/Trade/ExchangeLists/MarkPrice';
import { ShareModalType } from '../../Share/ShareModal';
import PositionSummaryDetail from './PositionSummaryDetail';

const PositionSummaryModal = () => {
    const { openModal, closeModal, setModalData, modalData } = useModalState();

    const onClose = () => closeModal('positionSummaryModal');

    const tokenStats = useTokenStats();

    const { userFee } = useUserFee();

    const position: UserPosition | undefined = modalData.data;

    const closeFee = useMemo(
        () =>
            parseEther(position?.volume || '0')
                .mul(userFee || 0)
                .div(100_00),
        [position?.volume, userFee]
    );

    if (!position?.id) return null;

    const pnlValues = calculatePnl(
        position,
        tokenStats?.[position.tradePair.chartSymbol]?.price
    );

    const handleShare = async () => {
        if (!pnlValues || !tokenStats) return;

        try {
            onClose();

            const userPosition: UserPosition & { markPrice: string } = {
                ...position,
                ...pnlValues,
                markPrice: tokenStats?.[position.tradePair.chartSymbol]?.price,
            };

            openModal('shareOpenPositionModal');
            setModalData((prev) => ({
                ...prev,
                data: {
                    position: userPosition,
                    type: ShareModalType.OpenPosition,
                },
            }));
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <CustomModal modalName='positionSummaryModal' className='position-summary-modal'>
            <CustomModalHeader title='Position Summary' onClose={onClose} />
            <CustomModalContent>
                <AssetType
                    tradePair={position.tradePair}
                    isShort={position.isShort}
                    withUSD
                />
                <div className='position-summary-details-container'>
                    <PositionSummaryDetail
                        title='Entry Leverage'
                        value={`${formatPercentages(position.entryLeverage)}x`}
                    />
                    <PositionSummaryDetail
                        title='Current Leverage'
                        value={`${formatPercentages(position.leverage)}x`}
                    />
                    <PositionSummaryDetail
                        title='Entry Collateral'
                        value={formatAmountWithSuffix(position.entryMargin || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    />
                    <PositionSummaryDetail
                        title='Current Collateral'
                        value={formatAmountWithSuffix(position.margin || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    />
                    <PositionSummaryDetail
                        title='Size'
                        value={formatAmountWithSuffix(position.volume || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    />
                    <PositionSummaryDetail
                        title='Entry Price'
                        value={formatUSD(position.entryPrice || 0, {
                            minDecimals: 2,
                            maxDecimals: position.tradePair.decimalFormatting,
                        })}
                    />
                    <PositionSummaryDetail
                        title='Mark Price'
                        value={<MarkPrice pool={position.tradePair} />}
                    />
                    <PositionSummaryDetail
                        title='Liquidation Price'
                        value={formatUSD(position.bankruptcyPrice || 0, {
                            minDecimals: 2,
                            maxDecimals: position.tradePair.decimalFormatting,
                        })}
                    />
                    <PositionSummaryDetail
                        title='PnL'
                        value={
                            <span
                                className={clsx({
                                    increased: pnlValues && +pnlValues.pnlGross > 0,
                                    decreased: pnlValues && +pnlValues.pnlGross < 0,
                                })}
                            >
                                {formatUSD(pnlValues?.pnlGross || 0, { minDecimals: 2 })}
                            </span>
                        }
                    />
                    <PositionSummaryDetail
                        title='Borrow Fees'
                        value={
                            <span
                                className={clsx({
                                    increased: parseUnits(position.borrowFee)
                                        .mul('-1')
                                        .gt(0),
                                    decreased: parseUnits(position.borrowFee)
                                        .mul('-1')
                                        .lt(0),
                                })}
                            >
                                {formatUSD(
                                    +position.borrowFee *
                                        (parseUnits(position.borrowFee).isZero()
                                            ? 1
                                            : -1),
                                    { minDecimals: 2 }
                                )}
                            </span>
                        }
                    />
                    <PositionSummaryDetail
                        title='Funding Fees'
                        value={
                            <span
                                className={clsx({
                                    increased: parseUnits(position.fundingFee)
                                        .mul('-1')
                                        .gt(0),
                                    decreased: parseUnits(position.fundingFee)
                                        .mul('-1')
                                        .lt(0),
                                })}
                            >
                                {formatUSD(+position.fundingFee * -1, { minDecimals: 2 })}
                            </span>
                        }
                    />
                    <PositionSummaryDetail
                        title='Total PnL'
                        value={
                            <span
                                className={clsx({
                                    increased: pnlValues && +pnlValues.pnl > 0,
                                    decreased: pnlValues && +pnlValues.pnl < 0,
                                })}
                            >
                                {formatUSD(pnlValues?.pnl || 0, { minDecimals: 2 })}
                            </span>
                        }
                    />
                    <PositionSummaryDetail
                        title='Closing Fees'
                        value={formatUSD(formatEther(closeFee || '0'), {
                            minDecimals: 2,
                        })}
                    />
                </div>
                <Button className='share-position-button' onClick={handleShare}>
                    Share Position
                </Button>
            </CustomModalContent>
        </CustomModal>
    );
};

export default PositionSummaryModal;
